import { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { Upload, Trash2, Wand2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";

const CATS = ["solo", "couple", "family", "pet", "wedding", "friends", "gifts", "anniversary", "special_occasion", "party"];

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("solo");
  const [uploading, setUploading] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onDrop = useCallback(async (files) => {
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/admin/gallery", { file_id: res.data.id, title, category: cat });
      } catch { toast.error(`Failed: ${file.name}`); }
    }
    setUploading(false); setTitle(""); toast.success("Added to gallery!"); load();
  }, [title, cat]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] } });

  const del = async (id) => { await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); load(); };
  const setCategory = async (id, category) => {
    try { await api.put(`/admin/gallery/${id}/category`, { category }); load(); } catch { toast.error("Failed"); }
  };
  const autoCategorize = async () => {
    setAutoRunning(true);
    try { const r = await api.post("/admin/gallery/auto-categorize"); toast.success(`AI tagged ${r.data.updated} images`); load(); }
    catch { toast.error("Auto-categorize failed"); } finally { setAutoRunning(false); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl mb-1">Gallery</h1>
          <p className="text-ink/60">Drag & drop examples; tag them by category for the public filters.</p>
        </div>
        <button onClick={autoCategorize} disabled={autoRunning} className="brut-btn !py-2 !px-4 bg-grape text-white text-sm" data-testid="gallery-auto-categorize">
          <Wand2 className="w-4 h-4" /> {autoRunning ? "Tagging…" : "Auto-categorize with AI"}
        </button>
      </div>

      <div className="brut-card p-6 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="font-display font-semibold text-sm">Caption for next upload(s)</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="brut-input mt-1" placeholder="e.g. Anniversary couple" data-testid="gallery-caption" />
          </div>
          <div>
            <label className="font-display font-semibold text-sm">Category for next upload(s)</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="brut-input mt-1 w-full capitalize" data-testid="gallery-upload-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div {...getRootProps()} data-testid="gallery-dropzone"
          className={`border-[3px] border-dashed border-ink rounded-2xl p-10 text-center cursor-pointer transition-colors ${isDragActive ? "bg-sky/40" : "bg-cream-solid hover:bg-sun/30"}`}>
          <input {...getInputProps()} />
          <Upload className="w-10 h-10 mx-auto mb-2" />
          <p className="font-display font-semibold">{isDragActive ? "Drop to upload!" : "Drag & drop images here, or click to browse"}</p>
        </div>
        {uploading && <p className="mt-3 font-semibold text-tangerine">Uploading…</p>}
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((g) => (
            <div key={g.id} className="relative brut border-ink overflow-hidden bg-white" data-testid={`gallery-admin-${g.id.slice(0,8)}`}>
              <img src={g.url && g.url.startsWith("http") ? g.url : `${API}/files/${g.file_id}`} alt={g.title} className="w-full h-40 object-cover" />
              {g.title && <p className="px-2 pt-2 text-sm font-semibold truncate">{g.title}</p>}
              <select value={CATS.includes(g.category) ? g.category : "solo"} onChange={(e) => setCategory(g.id, e.target.value)}
                className="brut-input !py-1 text-xs m-2 w-[calc(100%-1rem)] capitalize" data-testid={`gallery-cat-${g.id.slice(0,8)}`}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button onClick={() => del(g.id)} className="absolute top-2 right-2 bg-white border-2 border-ink rounded-full p-1.5 hover:bg-bubble hover:text-white" data-testid={`gallery-delete-${g.id.slice(0,8)}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
